import { useCallback, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';

import isRunningOnClient from 'js/utils/is-running-on-client';
// eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-assignment
const { Loader } = isRunningOnClient ? require('google-maps') : {};

import mapOptions from './map-options';

import Button from 'components/button/Button';
import MapPopup from './MapPopup';

import markerIcon from 'images/map-marker.png';

import { IMap, IMap_Markers } from 'source/NFR.types';

const Map: React.FC<IMap> = ({ googleMapsAPIKey, labels, markers = [] }) => {
  const googleRef = useRef<typeof google | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const getBounds = useCallback((markersToUse: IMap_Markers[]): google.maps.LatLngBounds | null => {
    const googleMaps = googleRef.current;
    if (!googleMaps) return null;

    const bounds = new googleMaps.maps.LatLngBounds();

    if (markersToUse && markersToUse.length) {
      markersToUse.forEach(marker => {
        bounds.extend(new googleMaps.maps.LatLng(marker.latitude, marker.longitude));
      });
    }

    return bounds;
  }, []);

  const fitMapToMarkers = useCallback(
    (markersToUse: IMap_Markers[]) => {
      if (!mapRef.current) return;

      const bounds = getBounds(markersToUse);
      if (!bounds) return;

      if (markersToUse.length === 1) {
        mapRef.current.setCenter(bounds.getCenter());
        mapRef.current.setZoom(16);
      } else {
        // fit to bounds
        mapRef.current.fitBounds(bounds);
      }
    },
    [getBounds]
  );

  const zoomIn = () => {
    if (!mapRef.current) return;

    const currentZoom = mapRef.current.getZoom();
    if (currentZoom !== undefined) {
      mapRef.current.setZoom(currentZoom + 1);
    }
  };

  const zoomOut = () => {
    if (!mapRef.current) return;

    const currentZoom = mapRef.current.getZoom();
    if (currentZoom !== undefined) {
      mapRef.current.setZoom(currentZoom - 1);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const loader = new Loader(googleMapsAPIKey);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    void loader.load().then((googleMaps: typeof google) => {
      if (!wrapperRef.current) return;

      googleRef.current = googleMaps;
      mapRef.current = new googleMaps.maps.Map(wrapperRef.current, mapOptions);

      fitMapToMarkers(markers || []);
      const popupsAndMarkers = (markers || []).map(marker => {
        return {
          popup: new googleMaps.maps.InfoWindow({
            content: ReactDOMServer.renderToString(<MapPopup {...marker.popup} />)
          }),
          marker: new googleMaps.maps.Marker({
            position: {
              lat: marker.latitude,
              lng: marker.longitude
            },
            icon: markerIcon,
            title: marker.name,
            map: mapRef.current
          })
        };
      });
      popupsAndMarkers.forEach(({ popup, marker }) => {
        marker.addListener('click', () => {
          // Close all popups:
          popupsAndMarkers.forEach(({ popup }) => popup.close());
          // Open popup for the clicked marker:
          popup.open(mapRef.current, marker);
        });
      });
    });
  }, [fitMapToMarkers, googleMapsAPIKey, markers]);

  useEffect(() => {
    if (mapRef.current) {
      fitMapToMarkers(markers || []);
    }
  }, [fitMapToMarkers, markers]);

  return (
    <div className="map">
      <div className="map--container" ref={wrapperRef} />

      <div className="map--controls">
        <Button className="map--button" icon="plus-small" onClick={zoomIn} title={labels?.zoomIn} />
        <Button className="map--button" icon="minus-small" onClick={zoomOut} title={labels?.zoomOut} />
      </div>
    </div>
  );
};

Map.displayName = 'Map';

export default Map;
